// Admin Service - Super-Admin functionality for managing moderators

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from '@firebase/firestore';
import type { Place } from '../types';
import { db } from './firebase';

const ADMINS_COLLECTION = 'admins';
const MODERATORS_COLLECTION = 'moderators';

export interface Admin {
  id: string;
  email: string;
  name: string;
  createdAt: Date;
}

export interface Moderator {
  id: string;
  email: string;
  name: string;
  addedBy: string;
  addedAt: Date;
}

/**
 * Check if a user is a super-admin
 * Returns false silently if collection doesn't exist or user has no access
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  if (!userId) return false;

  try {
    const docRef = doc(db, ADMINS_COLLECTION, userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    // Silently return false - user is not an admin or collection doesn't exist
    return false;
  }
}

/**
 * Get all moderators
 */
export async function getModeratorList(): Promise<Moderator[]> {
  try {
    const snapshot = await getDocs(
      query(collection(db, MODERATORS_COLLECTION), orderBy('addedAt', 'desc'))
    );
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Moderator[];
  } catch (error) {
    console.error('Error fetching moderators:', error);
    return [];
  }
}

/**
 * Subscribe to moderators list (real-time updates)
 */
export function subscribeToModerators(
  callback: (moderators: Moderator[]) => void
): Unsubscribe {
  const q = query(collection(db, MODERATORS_COLLECTION), orderBy('addedAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const moderators = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Moderator[];
    callback(moderators);
  }, (error) => {
    console.error('Error subscribing to moderators:', error);
    callback([]);
  });
}

/**
 * Add a new moderator by user ID
 * Only admins can add moderators
 */
export async function addModerator(
  adminId: string,
  userId: string,
  email: string,
  name: string
): Promise<void> {
  // Verify admin status
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Moderatoren hinzufuegen');
  }

  // Check if already a moderator
  const existingMod = await getDoc(doc(db, MODERATORS_COLLECTION, userId));
  if (existingMod.exists()) {
    throw new Error('Diese Person ist bereits Moderator');
  }

  // Add moderator
  await setDoc(doc(db, MODERATORS_COLLECTION, userId), {
    email,
    name,
    addedBy: adminId,
    addedAt: serverTimestamp(),
  });
}

/**
 * Remove a moderator
 * Only admins can remove moderators
 */
export async function removeModerator(
  adminId: string,
  moderatorId: string
): Promise<void> {
  // Verify admin status
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Moderatoren entfernen');
  }

  // Remove moderator
  await deleteDoc(doc(db, MODERATORS_COLLECTION, moderatorId));
}

/**
 * Get admin statistics
 */
export async function getAdminStats(): Promise<{
  totalModerators: number;
  totalPlaces: number;
  pendingPlaces: number;
  approvedPlaces: number;
}> {
  try {
    const [moderatorsSnap, placesSnap] = await Promise.all([
      getDocs(collection(db, MODERATORS_COLLECTION)),
      getDocs(collection(db, 'places')),
    ]);

    const places = placesSnap.docs.map((doc) => doc.data());
    const pendingPlaces = places.filter((p) => p.status === 'pending').length;
    const approvedPlaces = places.filter((p) => p.status === 'approved').length;

    return {
      totalModerators: moderatorsSnap.size,
      totalPlaces: placesSnap.size,
      pendingPlaces,
      approvedPlaces,
    };
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    return {
      totalModerators: 0,
      totalPlaces: 0,
      pendingPlaces: 0,
      approvedPlaces: 0,
    };
  }
}

/**
 * Update a place as admin (bypasses normal approval workflow)
 */
export async function updatePlaceAsAdmin(
  placeId: string,
  updates: Partial<Place>
): Promise<void> {
  try {
    const placeRef = doc(db, 'places', placeId);

    // Filter out undefined values (Firestore doesn't accept undefined)
    const cleanedUpdates: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(updates)) {
      if (value !== undefined) {
        cleanedUpdates[key] = value;
      }
    }

    // Add updated timestamp
    const updateData = {
      ...cleanedUpdates,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(placeRef, updateData);
  } catch (error) {
    console.error('Error updating place as admin:', error);
    throw new Error('Fehler beim Aktualisieren des Ortes');
  }
}

/**
 * Get all places for admin management
 */
export async function getAllPlacesForAdmin(): Promise<Place[]> {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'places'), orderBy('createdAt', 'desc'))
    );
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Place[];
  } catch (error) {
    console.error('Error fetching places for admin:', error);
    return [];
  }
}

/**
 * Create a new place as admin (bypasses normal approval workflow)
 */
export async function createPlaceAsAdmin(
  placeData: Partial<Place>
): Promise<Place> {
  try {
    // Generate a new document reference
    const placesRef = collection(db, 'places');
    const newPlaceRef = doc(placesRef);

    // Filter out undefined values
    const cleanedData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(placeData)) {
      if (value !== undefined) {
        cleanedData[key] = value;
      }
    }

    // Build the place document
    const newPlace: Record<string, unknown> = {
      ...cleanedData,
      id: newPlaceRef.id,
      status: 'approved',
      visibility: 'public',
      createdBy: 'admin-import',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      rating: 0,
      reviewCount: 0,
      moods: cleanedData.moods || [],
      activities: cleanedData.activities || [],
      community: cleanedData.community || [],
      openingHours: cleanedData.openingHours || { displayText: '' },
    };

    // Create the document
    await setDoc(newPlaceRef, newPlace);

    // Return the created place (with timestamps as dates for immediate use)
    return {
      ...newPlace,
      id: newPlaceRef.id,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Place;
  } catch (error) {
    console.error('Error creating place as admin:', error);
    throw new Error('Fehler beim Erstellen des Ortes');
  }
}
